import React, { useState, useEffect } from "react";
import { Container, Row, Col } from "react-bootstrap";
import { useParams } from "react-router-dom";
import {
  Box,
  Typography,
  Paper,
  Button,
  Alert,
  CircularProgress,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import { Add as AddIcon, ArrowUpward as SortIcon } from "@mui/icons-material";
import EditIcon from "../../../../src/assets/icons/EditIcon.svg";
import DeleteIcon from "../../../../src/assets/icons/DeleteIcon.svg";
import apiService from "../../../services/api";
import useCodeCommon from "../../../hooks/useCodeCommon";
import PlanEditForm from "./components/PlanEditForm";
import PlanCreateForm from "./components/PlanCreateForm";
import CustomLoading from "../../../components/common/CustomLoading";

interface VehicleModelCode {
  codeid: number;
  codevalue: string;
  codedisplayname: string;
  categoryid: number;
  codediscription: string;
  categoryname: string;
  displayorder: number;
}

const StatusChip = ({ value }: { value: string }) => {
  const styles: Record<string, any> = {
    Active: { bg: "rgba(212, 255, 199, 1)", color: "rgba(31, 113, 13, 1)" },
    InActive: { bg: "rgba(255, 227, 227, 1)", color: "rgba(197, 0, 0, 1)" },
    "No Plans": {
      bg: "rgba(255, 248, 220, 1)",
      color: "rgba(184, 134, 11, 1)",
    },
    Archived: { bg: "rgba(240, 240, 240, 1)", color: "rgba(100, 100, 100, 1)" },
    Draft: { bg: "rgba(255, 248, 220, 1)", color: "rgba(184, 134, 11, 1)" },
    "Loading...": { bg: "rgba(255, 248, 220, 1)", color: "rgba(184, 134, 11, 1)" },
    "Unknown": { bg: "rgba(255, 227, 227, 1)", color: "rgba(197, 0, 0, 1)" },
  };

  if (!value) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center" }}>
        <Chip
          label="Loading..."
          size="medium"
          sx={{
            backgroundColor: "rgba(255, 248, 220, 1)",
            color: "rgba(184, 134, 11, 1)",
            fontWeight: 600,
          }}
        />
      </Box>
    );
  }

  return (
    <Box sx={{ display: "flex", justifyContent: "center" }}>
      <Chip
        label={value}
        size="medium"
        sx={{
          backgroundColor: styles[value]?.bg || "rgba(240, 240, 240, 1)",
          color: styles[value]?.color || "rgba(100, 100, 100, 1)",
          fontWeight: 600,
        }}
      />
    </Box>
  );
};

const EditPlanPage: React.FC<{ onBack: () => void }> = ({ onBack }) => {
  const { hubmasterid } = useParams<{ hubmasterid: string }>();
  const {
    options: planStatusOptions,
    loading: planStatusLoading,
    error: planStatusError,
    refetch: refetchPlanStatus,
  } = useCodeCommon("PlanStatus", true);
  
  const [vehicleModelCodes, setVehicleModelCodes] = useState<VehicleModelCode[]>([]);
  const [vehicleModelLoading, setVehicleModelLoading] = useState(false);
  const [vehicleModelError, setVehicleModelError] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    planid: 0,
    vehicleModel: "",
    city: "",
    hub: "",
    planname: "",
    duration: "",
    popular: "",
    price: "",
    kmlimit: "",
    extraPerKm: "",
    deposit: "",
    lateTripPenalty: "",
    depositRefundTimeline: "",
    status: "",
    template: "",
    hubmasterid: "",
    templatemasterid: "",
    effectivefrom: "",
    statusid: "",
    evtypeid: "",
    days: "",
  });

  // Group plans by vehiclemodelid instead of evtypeid
  const [plansByVehicleModel, setPlansByVehicleModel] = useState<Record<number, any[]>>({});
  const [templates, setTemplates] = useState<any[]>([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [templatesError, setTemplatesError] = useState<string | null>(null);

  // Store new plan data for each vehicle model
  const [newPlansByVehicleModel, setNewPlansByVehicleModel] = useState<Record<number, any>>({});

  const [editingPlanId, setEditingPlanId] = useState<number | null>(null);
  const [editPlanData, setEditPlanData] = useState<any>(null);
  const [loadingPlan, setLoadingPlan] = useState(false);
  const [deletingPlanId, setDeletingPlanId] = useState<number | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [planToDelete, setPlanToDelete] = useState<{
    id: string | number;
    vehiclemodelid: number;
  } | null>(null);

  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const getStatusId = (status: string): number | null => {
    const statusOption = planStatusOptions.find(
      (option) => option.codedisplayname === status
    );
    return statusOption ? parseInt(statusOption.codevalue) : null;
  };

  const fetchTemplates = async (): Promise<void> => {
    try {
      setTemplatesLoading(true);
      setTemplatesError(null);
  
      const response: any = await apiService.getAllTemplates();
  
      const templatesData =
        (Array.isArray(response)
          ? response
          : response?.data ?? []);
  
      setTemplates(templatesData);
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : "Failed to load templates. Please try again.";
      setTemplatesError(message);
    } finally {
      setTemplatesLoading(false);
    }
  };

  const refreshCodeCommonData = async (): Promise<void> => {
    try {
      // Refresh plan status options
      await refetchPlanStatus();
      
      // Refresh templates
      await fetchTemplates();
    } catch (error) {
      console.error("Error refreshing code common data:", error);
    }
  };
  
  

  // Fetch vehicle models on component mount
  useEffect(() => {
    const fetchVehicleModels = async () => {
      try {
        setVehicleModelLoading(true);
        setVehicleModelError(null);
        const response: any = await apiService.getAllCodeCommonByCategory("VehicleModel");
        const raw = (response as any)?.data || response || [];
        const codes = Array.isArray(raw) ? raw : [];
        // Sort by displayorder
        const sortedCodes = [...codes].sort((a: VehicleModelCode, b: VehicleModelCode) => 
          a.displayorder - b.displayorder
        );
        setVehicleModelCodes(sortedCodes);
      } catch (err: any) {
        console.error('Error fetching VehicleModel codes:', err);
        setVehicleModelError(err.message || 'Failed to fetch vehicle model options');
        setVehicleModelCodes([]);
      } finally {
        setVehicleModelLoading(false);
      }
    };

    fetchVehicleModels();
  }, []);

  useEffect(() => {
    const fetchPlansByHubId = async () => {
      if (!hubmasterid) {
        setError("Hub ID is required");
        setFetching(false);
        return;
      }

      try {
        setFetching(true);
        setError(null);

        const response: any = await apiService.getPlansByHubId(hubmasterid);

        if (response?.isSuccess === false) {
          setError(response.message || "Failed to fetch plans data");
          return;
        }

        const plansData = response?.data || response;
        if (plansData && Array.isArray(plansData)) {
          const transformedPlans = plansData.map((plan: any) =>
            transformApiDataToPlanData(plan)
          );

          // Group plans by vehiclemodelid
          const groupedPlans: Record<number, any[]> = {};
          transformedPlans.forEach((plan: any) => {
            const modelId = plan.vehiclemodelid;
            if (modelId !== null && modelId !== undefined) {
              if (!groupedPlans[modelId]) {
                groupedPlans[modelId] = [];
              }
              groupedPlans[modelId].push(plan);
            }
          });

          setPlansByVehicleModel(groupedPlans);
        } else {
          setPlansByVehicleModel({});
        }
      } catch (err: any) {
        setError(err.message || "Failed to fetch plans data");
      } finally {
        setFetching(false);
      }
    };

    fetchPlansByHubId();
    fetchTemplates();
  }, [hubmasterid]);

  // Re-map plans when planStatusOptions loads
  useEffect(() => {
    setPlansByVehicleModel(prev => {
      const hasPlans = Object.keys(prev).length > 0;
      if (!hasPlans || planStatusLoading || !planStatusOptions || planStatusOptions.length === 0) {
        return prev;
      }
      const updated: Record<number, any[]> = {};
      Object.keys(prev).forEach(modelId => {
        updated[Number(modelId)] = prev[Number(modelId)].map(plan => ({
          ...plan,
          status: getStatusDisplayName(plan.statusid)
        }));
      });
      return updated;
    });
  }, [planStatusOptions, planStatusLoading]);

  // Re-map plans when templates load
  useEffect(() => {
    setPlansByVehicleModel(prev => {
      const hasPlans = Object.keys(prev).length > 0;
      if (!hasPlans || templatesLoading || !templates || templates.length === 0) {
        return prev;
      }
      const updated: Record<number, any[]> = {};
      Object.keys(prev).forEach(modelId => {
        updated[Number(modelId)] = prev[Number(modelId)].map(plan => ({
          ...plan,
          template: getTemplateDisplayName(plan.templatemasterid)
        }));
      });
      return updated;
    });
  }, [templates, templatesLoading]);

  // Re-map plans when vehicle model codes load
  useEffect(() => {
    setPlansByVehicleModel(prev => {
      const hasPlans = Object.keys(prev).length > 0;
      if (!hasPlans || !vehicleModelCodes || vehicleModelCodes.length === 0) {
        return prev;
      }
      const updated: Record<number, any[]> = {};
      Object.keys(prev).forEach(modelId => {
        updated[Number(modelId)] = prev[Number(modelId)].map(plan => ({
          ...plan,
          vehicleModelName: getVehicleModelNameByCodeValue(plan.vehiclemodelid)
        }));
      });
      return updated;
    });
  }, [vehicleModelCodes]);


  const getStatusDisplayName = (planstatusid: number): string => {
    if (planStatusOptions && planStatusOptions.length > 0) {
      const statusOption = planStatusOptions.find(option => 
        parseInt(option.codevalue) === planstatusid
      );
      return statusOption ? statusOption.codedisplayname : 'Unknown';
    }
    // If status options are not loaded yet, return a generic status
    // This prevents "Loading..." from showing when the API already provides the status
    return 'Active'; // Default fallback
  };

  const getTemplateDisplayName = (templatemasterid: number): string => {
    if (templates && templates.length > 0) {
      const templateOption = templates.find(template => 
        template.ratetemplatemasterid === templatemasterid
      );
      return templateOption ? templateOption.templatename : '';
    }
    return '';
  };

  const getVehicleModelDisplayName = (codeValue: string | number | null | undefined): string => {
    if (codeValue === null || codeValue === undefined) return '';
    const code = vehicleModelCodes.find(c => c.codevalue === String(codeValue));
    return code?.codedisplayname || '';
  };

  const getVehicleModelNameByCodeValue = (vehiclemodelid: number | string | null | undefined): string => {
    if (vehiclemodelid === null || vehiclemodelid === undefined) return '';
    return getVehicleModelDisplayName(vehiclemodelid) || '';
  };

  

  const transformApiDataToPlanData = (apiData: any) => {
    const getDuration = (days: number) => {
      return `${days} Days`;
    };

    return {
      id: apiData.planid?.toString() || Date.now().toString(),
      planName: apiData.planname || "",
      duration: getDuration(apiData.days || 0),
      kmLimit: apiData.kmlimit?.toString() || "0",
      price: apiData.price?.toString() || "0",
      processing_fees: apiData.processing_fees?.toString() || "0",
      refundable_amount: apiData.refundable_amount?.toString() || "0",
      template: getTemplateDisplayName(apiData.templatemasterid),
      popular: apiData.popular ? "True" : "False",
      evTypeId: apiData.evtypeid,
      hubmasterid: apiData.hubmasterid || 0,
      templatemasterid: apiData.templatemasterid,
      effectivefrom: apiData.effectivefrom || "",
      status: apiData.status || getStatusDisplayName(apiData.statusid), // Use raw status first, fallback to mapping
      statusid: apiData.statusid,
      days: apiData.days || 0,
      vehiclemodelid: apiData.vehiclemodelid || null,
      vehicleModelName: getVehicleModelNameByCodeValue(apiData.vehiclemodelid),
    };
  };

  const handleNewPlanChange = (
    vehiclemodelid: number,
    field: string,
    value: string
  ) => {
    setNewPlansByVehicleModel((prev) => ({
      ...prev,
      [vehiclemodelid]: {
        ...(prev[vehiclemodelid] || {
          planName: "",
          duration: "",
          kmLimit: "",
          price: "",
          processing_fees: "",
          refundable_amount: "",
          template: "",
          status: "",
          popular: "",
        }),
        [field]: value,
      },
    }));
  };

  const handleAddPlan = async (vehiclemodelid: number) => {
    const newPlan = newPlansByVehicleModel[vehiclemodelid] || {
      planName: "",
      duration: "",
      kmLimit: "",
      price: "",
      processing_fees: "",
      refundable_amount: "",
      template: "",
      status: "",
      popular: "",
    };

    if (!newPlan.planName.trim()) {
      setError("Plan name is required");
      return;
    }

    if (!newPlan.duration) {
      setError("Duration is required");
      return;
    }

    if (!newPlan.kmLimit || parseInt(newPlan.kmLimit) <= 0) {
      setError("Valid KM limit is required");
      return;
    }

    if (!newPlan.price || parseInt(newPlan.price) <= 0) {
      setError("Valid price is required");
      return;
    }

    if (!newPlan.template || newPlan.template === "") {
      setError("Template is required");
      return;
    }

    if (!newPlan.status) {
      setError("Status is required");
      return;
    }

    if (!newPlan.popular) {
      setError("Popular selection is required");
      return;
    }

    const templateId = Number(newPlan.template);
    if (!Number.isFinite(templateId)) {
      setError("Valid template is required");
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const statusId = getStatusId(newPlan.status);
      if (statusId === null) {
        setError("Status options not available. Please try again later.");
        setLoading(false);
        return;
      }

      // Get evtypeid from existing plans in the same vehicle model group, or default to 2 (low speed)
      const existingPlans = plansByVehicleModel[vehiclemodelid] || [];
      const evTypeId = existingPlans.length > 0 ? existingPlans[0].evTypeId : 2;

      const payload = {
        planid: 0,
        hubmasterid: parseInt(hubmasterid || "0"),
        planname: newPlan.planName,
        templatemasterid: templateId,
        ratetemplatemasterid: templateId,
        effectivefrom: new Date().toISOString(),
        popular: newPlan.popular === "True",
        statusid: statusId,
        evtypeid: evTypeId,
        vehiclemodelid: vehiclemodelid,
        kmlimit: parseInt(newPlan.kmLimit) || 0,
        days: parseInt(newPlan.duration) || 0,
        price: parseInt(newPlan.price) || 0,
        processing_fees: parseFloat(newPlan.processing_fees) || 0,
        refundable_amount: parseFloat(newPlan.refundable_amount) || 0,
      };

      const response: any = await apiService.createPlan(payload);

      if (response?.isSuccess === false) {
        setError(response.message || "Failed to create plan.");
        return;
      }

      const createdPlanData = response?.data || response;
      
      const planData = {
        id: createdPlanData?.planid?.toString() || Date.now().toString(),
        planName: newPlan.planName,
        duration: newPlan.duration ,
        kmLimit: newPlan.kmLimit,
        price: newPlan.price,
        processing_fees: newPlan.processing_fees || "0",
        refundable_amount: newPlan.refundable_amount || "0",
        template: getTemplateDisplayName(templateId),
        status: newPlan.status,
        popular: newPlan.popular,
        evTypeId: evTypeId,
        hubmasterid: parseInt(hubmasterid || "0"),
        templatemasterid: templateId,
        effectivefrom: new Date().toISOString(),
        statusid: getStatusId(newPlan.status),
        days: parseInt(newPlan.duration) || 0,
        vehiclemodelid: vehiclemodelid,
        vehicleModelName: getVehicleModelNameByCodeValue(vehiclemodelid),
      };

      setPlansByVehicleModel((prev) => ({
        ...prev,
        [vehiclemodelid]: [...(prev[vehiclemodelid] || []), planData],
      }));

      setNewPlansByVehicleModel((prev) => ({
        ...prev,
        [vehiclemodelid]: {
          planName: "",
          duration: "",
          kmLimit: "",
          price: "",
          processing_fees: "",
          refundable_amount: "",
          template: "",
          status: "",
          popular: "",
        },
      }));

      setSuccess("Plan created successfully!");
      
      // Refresh code common data
      await refreshCodeCommonData();
      
      // Refresh plans data
      const refreshResponse: any = await apiService.getPlansByHubId(hubmasterid || "0");
      if (refreshResponse?.isSuccess !== false) {
        const refreshedPlansData = refreshResponse?.data || refreshResponse;
        if (refreshedPlansData && Array.isArray(refreshedPlansData)) {
          const transformedPlans = refreshedPlansData.map((plan: any) =>
            transformApiDataToPlanData(plan)
          );

          // Group plans by vehiclemodelid
          const groupedPlans: Record<number, any[]> = {};
          transformedPlans.forEach((plan: any) => {
            const modelId = plan.vehiclemodelid;
            if (modelId !== null && modelId !== undefined) {
              if (!groupedPlans[modelId]) {
                groupedPlans[modelId] = [];
              }
              groupedPlans[modelId].push(plan);
            }
          });

          setPlansByVehicleModel(groupedPlans);
        }
      }
    } catch (err: any) {
      setError(err.message || "Failed to create plan. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleEditPlan = async (planId: number) => {
    try {
      setLoadingPlan(true);
      setError(null);

      const response: any = await apiService.getPlanById(planId.toString());

      if (response?.isSuccess === false) {
        setError(response.message || "Failed to fetch plan details");
        return;
      }

      const planData = response?.data || response;

      setEditingPlanId(planId);
      setEditPlanData({
        planName: planData.planname || "",
        duration: String(planData.days ?? 0),
        kmLimit: planData.kmlimit?.toString() || "",
        price: planData.price?.toString() || "",
        processing_fees: planData.processing_fees?.toString() || "",
        refundable_amount: planData.refundable_amount?.toString() || "",
        template: planData.templatemasterid,
        status: getStatusDisplayName(planData.statusid),
        popular: planData.popular ? "True" : "False",
        planid: planData.planid || 0,
        hubmasterid: planData.hubmasterid || 0,
        templatemasterid: planData.templatemasterid,
        effectivefrom: planData.effectivefrom || "",
        statusid: planData.statusid || 1,
        evtypeid: planData.evtypeid || 1,
        vehiclemodelid: planData.vehiclemodelid || null,
        days: planData.days || 0,
      });
    } catch (err: any) {
      setError(err.message || "Failed to fetch plan details");
    } finally {
      setLoadingPlan(false);
    }
  };

  const handleEditPlanChange = (field: string, value: string) => {
    setEditPlanData((prev: any) => ({ ...prev, [field]: value }));
  };

  const handleUpdatePlan = async (vehiclemodelid: number) => {
    if (!editPlanData) return;

    if (!editPlanData.planName.trim()) {
      setError("Plan name is required");
      return;
    }

    if (!editPlanData.duration) {
      setError("Duration is required");
      return;
    }

    if (!editPlanData.kmLimit || parseInt(editPlanData.kmLimit) <= 0) {
      setError("Valid KM limit is required");
      return;
    }

    if (!editPlanData.price || parseInt(editPlanData.price) <= 0) {
      setError("Valid price is required");
      return;
    }

    if (!editPlanData.template) {
      setError("Template is required");
      return;
    }

    if (!editPlanData.status) {
      setError("Status is required");
      return;
    }

    if (!editPlanData.popular) {
      setError("Popular selection is required");
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const statusId = getStatusId(editPlanData.status);
      if (statusId === null) {
        setError("Status options not available. Please try again later.");
        setLoading(false);
        return;
      }

      const payload = {
        planid: editPlanData.planid || 0,
        hubmasterid: editPlanData.hubmasterid || parseInt(hubmasterid || "0"),
        planname: editPlanData.planName,
        templatemasterid: Number(editPlanData.template),
        ratetemplatemasterid: Number(editPlanData.template),
        effectivefrom: editPlanData.effectivefrom || new Date().toISOString(),
        popular: editPlanData.popular === "True",
        statusid: statusId,
        evtypeid: editPlanData.evtypeid || 1,
        vehiclemodelid: editPlanData.vehiclemodelid || vehiclemodelid,
        kmlimit: parseInt(editPlanData.kmLimit) || 0,
        days: parseInt(editPlanData.duration) || 0,
        price: parseInt(editPlanData.price) || 0,
        processing_fees: editPlanData.processing_fees ? parseFloat(editPlanData.processing_fees) : 0,
        refundable_amount: editPlanData.refundable_amount ? parseFloat(editPlanData.refundable_amount) : 0,
      };

      const response: any = await apiService.updatePlan(
        editingPlanId?.toString() || "",
        payload
      );

      if (response?.isSuccess === false) {
        setError(response.message || "Failed to update plan.");
        return;
      }

      const updatedPlanData = {
        id: editingPlanId,
        planName: editPlanData.planName,
        duration: `${parseInt(editPlanData.duration) || 0} Day${parseInt(editPlanData.duration) === 1 ? "" : "s"}`,
        kmLimit: editPlanData.kmLimit,
        price: editPlanData.price,
        processing_fees: editPlanData.processing_fees || "0",
        refundable_amount: editPlanData.refundable_amount || "0",
        template: getTemplateDisplayName(parseInt(editPlanData.template)),
        status: editPlanData.status,
        popular: editPlanData.popular,
        evTypeId: editPlanData.evtypeid,
        hubmasterid: editPlanData.hubmasterid,
        templatemasterid: parseInt(editPlanData.template),
        effectivefrom: editPlanData.effectivefrom,
        statusid: getStatusId(editPlanData.status) ?? undefined,
        days: parseInt(editPlanData.duration) || 0,
        vehiclemodelid: editPlanData.vehiclemodelid || vehiclemodelid,
        vehicleModelName: getVehicleModelNameByCodeValue(editPlanData.vehiclemodelid || vehiclemodelid),
      };

      setPlansByVehicleModel((prev) => ({
        ...prev,
        [vehiclemodelid]: (prev[vehiclemodelid] || []).map((plan) =>
          plan.id === editingPlanId ? updatedPlanData : plan
        ),
      }));

      setSuccess("Plan updated successfully!");
      setEditingPlanId(null);
      setEditPlanData(null);
      
      // Refresh code common data
      await refreshCodeCommonData();
      
      // Refresh plans data
      const refreshResponse: any = await apiService.getPlansByHubId(hubmasterid || "0");
      if (refreshResponse?.isSuccess !== false) {
        const refreshedPlansData = refreshResponse?.data || refreshResponse;
        if (refreshedPlansData && Array.isArray(refreshedPlansData)) {
          const transformedPlans = refreshedPlansData.map((plan: any) =>
            transformApiDataToPlanData(plan)
          );

          // Group plans by vehiclemodelid
          const groupedPlans: Record<number, any[]> = {};
          transformedPlans.forEach((plan: any) => {
            const modelId = plan.vehiclemodelid;
            if (modelId !== null && modelId !== undefined) {
              if (!groupedPlans[modelId]) {
                groupedPlans[modelId] = [];
              }
              groupedPlans[modelId].push(plan);
            }
          });

          setPlansByVehicleModel(groupedPlans);
        }
      }
    } catch (err: any) {
      setError(err.message || "Failed to update plan. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleCancelEdit = () => {
    setEditingPlanId(null);
    setEditPlanData(null);
  };

  const handleDeletePlan = (
    vehiclemodelid: number,
    planId: string | number
  ) => {
    setPlanToDelete({ id: planId, vehiclemodelid });
    setDeleteDialogOpen(true);
  };

  const confirmDeletePlan = async () => {
    if (!planToDelete) return;

    try {
      setDeletingPlanId(Number(planToDelete.id));
      setError(null);

      const response: any = await apiService.deletePlan(
        planToDelete.id.toString()
      );

      if (response?.isSuccess === false) {
        setError(response.message || "Failed to delete plan.");
        return;
      }

      setPlansByVehicleModel((prev) => ({
        ...prev,
        [planToDelete.vehiclemodelid]: (prev[planToDelete.vehiclemodelid] || []).filter(
          (plan) => plan.id !== planToDelete.id.toString()
        ),
      }));

      setSuccess("Plan deleted successfully!");
      setDeleteDialogOpen(false);
      setPlanToDelete(null);
      
      // Refresh code common data
      await refreshCodeCommonData();
      
      // Refresh plans data
      const refreshResponse: any = await apiService.getPlansByHubId(hubmasterid || "0");
      if (refreshResponse?.isSuccess !== false) {
        const refreshedPlansData = refreshResponse?.data || refreshResponse;
        if (refreshedPlansData && Array.isArray(refreshedPlansData)) {
          const transformedPlans = refreshedPlansData.map((plan: any) =>
            transformApiDataToPlanData(plan)
          );

          // Group plans by vehiclemodelid
          const groupedPlans: Record<number, any[]> = {};
          transformedPlans.forEach((plan: any) => {
            const modelId = plan.vehiclemodelid;
            if (modelId !== null && modelId !== undefined) {
              if (!groupedPlans[modelId]) {
                groupedPlans[modelId] = [];
              }
              groupedPlans[modelId].push(plan);
            }
          });

          setPlansByVehicleModel(groupedPlans);
        }
      }
    } catch (err: any) {
      setError(err.message || "Failed to delete plan. Please try again.");
    } finally {
      setDeletingPlanId(null);
    }
  };

  const cancelDelete = () => {
    setDeleteDialogOpen(false);
    setPlanToDelete(null);
  };

  const handleInputChange = (field: string, value: string | boolean) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  if (fetching || planStatusLoading || templatesLoading || vehicleModelLoading) {
    return <CustomLoading message="Fetching Template Data..." />;
  }

  const renderPlanTable = (
    plans: any[],
    newPlan: any,
    vehiclemodelid: number
  ) => (
    <div className="mb-4">
      <TableContainer 
        component={Paper} 
        sx={{ 
          boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
          borderRadius: "8px",
          overflow: "hidden"
        }}
      >
        <Table sx={{ 
          minWidth: 900,
          tableLayout: "fixed",
          "& .MuiTableCell-root": {
            verticalAlign: "middle"
          }
        }}>
          <TableHead>
            <TableRow sx={{ 
              backgroundColor: "rgba(191, 229, 255, 1)",
              "& .MuiTableCell-head": {
                borderBottom: "2px solid rgba(13,44,90,0.2)",
                padding: "8px 12px",
                fontWeight: 600,
                fontSize: "12px"
              }
            }}>
              <TableCell sx={{ fontWeight: "bold", color: "rgba(13,44,90,1)", textAlign: "center", width: "12%" }}>
                <Box className="d-flex align-items-center justify-content-center">
                  Plan Name
                  <SortIcon sx={{ ml: 0.5, fontSize: "14px", color: "rgba(13,44,90,0.7)" }} />
                </Box>
              </TableCell>
              <TableCell sx={{ fontWeight: "bold", color: "rgba(13,44,90,1)", textAlign: "center", width: "10%" }}>
                <Box className="d-flex align-items-center justify-content-center">
                  Duration
                  <SortIcon sx={{ ml: 0.5, fontSize: "14px", color: "rgba(13,44,90,0.7)" }} />
                </Box>
              </TableCell>
              <TableCell sx={{ fontWeight: "bold", color: "rgba(13,44,90,1)", textAlign: "center", width: "9%" }}>
                <Box className="d-flex align-items-center justify-content-center">
                  KM Limit
                  <SortIcon sx={{ ml: 0.5, fontSize: "14px", color: "rgba(13,44,90,0.7)" }} />
                </Box>
              </TableCell>
              <TableCell sx={{ fontWeight: "bold", color: "rgba(13,44,90,1)", textAlign: "center", width: "10%" }}>
                <Box className="d-flex align-items-center justify-content-center">
                  Price
                  <SortIcon sx={{ ml: 0.5, fontSize: "14px", color: "rgba(13,44,90,0.7)" }} />
                </Box>
              </TableCell>
              <TableCell sx={{ fontWeight: "bold", color: "rgba(13,44,90,1)", textAlign: "center", width: "12%" }}>
                <Box className="d-flex align-items-center justify-content-center">
                  Processing Fees
                  <SortIcon sx={{ ml: 0.5, fontSize: "14px", color: "rgba(13,44,90,0.7)" }} />
                </Box>
              </TableCell>
              <TableCell sx={{ fontWeight: "bold", color: "rgba(13,44,90,1)", textAlign: "center", width: "13%" }}>
                <Box className="d-flex align-items-center justify-content-center">
                  Refundable Amount
                  <SortIcon sx={{ ml: 0.5, fontSize: "14px", color: "rgba(13,44,90,0.7)" }} />
                </Box>
              </TableCell>
              <TableCell sx={{ fontWeight: "bold", color: "rgba(13,44,90,1)", textAlign: "center", width: "12%" }}>
                <Box className="d-flex align-items-center justify-content-center">
                  Template
                  <SortIcon sx={{ ml: 0.5, fontSize: "14px", color: "rgba(13,44,90,0.7)" }} />
                </Box>
              </TableCell>
              <TableCell sx={{ fontWeight: "bold", color: "rgba(13,44,90,1)", width: "10%", textAlign: "center" }}>
                <Box className="d-flex align-items-center justify-content-center">
                  Status
                  <SortIcon sx={{ ml: 0.5, fontSize: "14px", color: "rgba(13,44,90,0.7)" }} />
                </Box>
              </TableCell>
              <TableCell sx={{ fontWeight: "bold", color: "rgba(13,44,90,1)", width: "8%", textAlign: "center" }}>
                <Box className="d-flex align-items-center justify-content-center">
                  Popular
                  <SortIcon sx={{ ml: 0.5, fontSize: "14px", color: "rgba(13,44,90,0.7)" }} />
                </Box>
              </TableCell>
              <TableCell
                sx={{
                  fontWeight: "bold",
                  color: "rgba(13,44,90,1)",
                  textAlign: "center",
                  width: "12%",
                  minWidth: "150px"
                }}
              >
                Action
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody sx={{
            "& .MuiTableCell-root": {
              padding: "8px 12px",
              borderBottom: "1px solid rgba(0,0,0,0.08)",
              fontSize: "13px",
              verticalAlign: "middle",
              textAlign: "center"
            },
            "& .MuiTableRow-root:hover": {
              backgroundColor: "rgba(13,44,90,0.02)"
            }
          }}>
            {plans.map((plan) => {
              const isEditing = editingPlanId === plan.id && editPlanData;

              if (isEditing) {
                return (
                  <PlanEditForm
                    key={plan.id}
                    editPlanData={editPlanData}
                    planStatusOptions={planStatusOptions}
                    templates={templates}
                    templatesLoading={templatesLoading}
                    loading={loading}
                    loadingPlan={loadingPlan}
                    onEditPlanChange={handleEditPlanChange}
                    onUpdatePlan={handleUpdatePlan}
                    onCancelEdit={handleCancelEdit}
                    section={vehiclemodelid}
                  />
                );
              }

              return (
                <TableRow 
                  key={plan.id} 
                  hover
                  sx={{
                    "&:nth-of-type(even)": {
                      backgroundColor: "rgba(248, 249, 250, 0.5)"
                    }
                  }}
                >
                  <TableCell sx={{ textAlign: "center" }}>{plan.planName}</TableCell>
                  <TableCell sx={{ textAlign: "center" }}>{plan.duration}</TableCell>
                  <TableCell sx={{ textAlign: "center" }}>{plan.kmLimit}</TableCell>
                  <TableCell sx={{ textAlign: "center" }}>₹{plan.price}</TableCell>
                  <TableCell sx={{ textAlign: "center" }}>₹{plan.processing_fees || "N/A"}</TableCell>
                  <TableCell sx={{ textAlign: "center" }}>₹{plan.refundable_amount || "N/A"}</TableCell>
                  <TableCell sx={{ textAlign: "center" }}>{plan.template}</TableCell>
                  <TableCell sx={{ textAlign: "center" }}>
                    <StatusChip value={plan.status} />
                  </TableCell>
                  {/* Decrease space for Popular column */}
                  <TableCell sx={{
                    fontWeight: 500,
                    color: plan.popular === "True" ? "rgba(31, 113, 13, 1)" : "rgba(100, 100, 100, 1)",
                    textAlign: "center"
                  }}>
                    {plan.popular}
                  </TableCell>
                  <TableCell sx={{ textAlign: "center", width: "15%", minWidth: "200px" }}>
                    <Box
                      sx={{ 
                        display: "flex", 
                        gap: 0.5, // Reduce gap between edit/delete buttons
                        justifyContent: "center",
                        alignItems: "center"
                      }}
                    >
                      <Button
                        variant="text"
                        sx={{ 
                          p: 1, 
                          minWidth: "auto",
                          borderRadius: "6px",
                          "&:hover": {
                            backgroundColor: "rgba(13,44,90,0.08)"
                          }
                        }}
                        disabled={loadingPlan || editingPlanId !== null}
                        onClick={() => handleEditPlan(plan.id)}
                      >
                        <img src={EditIcon} alt="Edit" width={25} height={25} />
                      </Button>
                      <Button
                        variant="text"
                        sx={{ 
                          p: 1, 
                          minWidth: "auto",
                          borderRadius: "6px",
                          "&:hover": {
                            backgroundColor: "rgba(197, 0, 0, 0.08)"
                          }
                        }}
                        disabled={
                          editingPlanId !== null ||
                          deletingPlanId === Number(plan.id)
                        }
                        onClick={() => handleDeletePlan(vehiclemodelid, plan.id)}
                      >
                        {deletingPlanId === Number(plan.id) ? (
                          <CircularProgress size={16} color="inherit" />
                        ) : (
                          <img
                            src={DeleteIcon}
                            alt="Delete"
                            width={22}
                            height={22}
                          />
                        )}
                      </Button>
                    </Box>
                  </TableCell>
                </TableRow>
              );
            })}

            <PlanCreateForm
              newPlan={newPlan}
              planStatusOptions={planStatusOptions}
              templates={templates}
              editingPlanId={editingPlanId}
              loading={loading}
              onNewPlanChange={handleNewPlanChange}
              onAddPlan={handleAddPlan}
              section={vehiclemodelid}
            />
          </TableBody>
        </Table>
      </TableContainer>

      <Box 
        className="d-flex justify-content-end mt-4"
        sx={{
          padding: "16px 0",
          borderTop: "2px solid rgba(13,44,90,0.1)",
          backgroundColor: "rgba(248, 249, 250, 0.3)"
        }}
      >
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleAddPlan(vehiclemodelid)}
          disabled={editingPlanId !== null || loading}
          sx={{
            backgroundColor: "rgba(13,44,90,1)",
            minWidth: "120px",
            height: "40px",
            borderRadius: "8px",
            fontSize: "14px",
            fontWeight: 600,
            textTransform: "none",
            boxShadow: "0 2px 4px rgba(13,44,90,0.3)",
            "&:hover": { 
              backgroundColor: "rgba(13,44,90,0.9)",
              boxShadow: "0 4px 8px rgba(13,44,90,0.4)"
            },
            "&:disabled": { 
              backgroundColor: "rgba(13,44,90,0.5)",
              boxShadow: "none"
            },
          }}
        >
          Add Plan
        </Button>
      </Box>
    </div>
  );

  // Get all vehicle models from API, sorted by displayorder
  const sortedVehicleModels = [...vehicleModelCodes].sort((a, b) => 
    a.displayorder - b.displayorder
  );

  return (
    <Container fluid>
      {sortedVehicleModels.map((modelCode) => {
        const vehiclemodelid = Number(modelCode.codevalue);
        const vehicleModelName = modelCode.codedisplayname;
        const plans = plansByVehicleModel[vehiclemodelid] || [];
        const hasPlans = plans.length > 0;

        const newPlan = newPlansByVehicleModel[vehiclemodelid] || {
          planName: "",
          duration: "",
          kmLimit: "",
          price: "",
          processing_fees: "",
          refundable_amount: "",
          template: "",
          status: "",
          popular: "",
        };

        return (
          <Paper key={vehiclemodelid} className="mb-4 shadow-sm border-0">
            <Box
              className="px-4 py-3 mb-0"
              sx={{
                backgroundColor: "rgba(13,44,90,1)",
                borderRadius: "8px 8px 0 0",
                boxShadow: "0 2px 4px rgba(13,44,90,0.2)"
              }}
            >
              <Typography variant="h6" className="fw-bold mb-0 text-white" sx={{ fontSize: "18px", fontWeight: 600 }}>
                {vehicleModelName}
              </Typography>
            </Box>
            <Box className="px-3 pt-3 pb-4">
              {hasPlans ? (
                renderPlanTable(plans, newPlan, vehiclemodelid)
              ) : (
                <Box>
                  <TableContainer 
                    component={Paper} 
                    sx={{ 
                      boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                      borderRadius: "8px",
                      overflow: "hidden"
                    }}
                  >
                    <Table sx={{ 
                      minWidth: 900,
                      tableLayout: "fixed",
                      "& .MuiTableCell-root": {
                        verticalAlign: "middle"
                      }
                    }}>
                      <TableHead>
                        <TableRow sx={{ 
                          backgroundColor: "rgba(191, 229, 255, 1)",
                          "& .MuiTableCell-head": {
                            borderBottom: "2px solid rgba(13,44,90,0.2)",
                            padding: "8px 12px",
                            fontWeight: 600,
                            fontSize: "12px"
                          }
                        }}>
                          <TableCell sx={{ fontWeight: "bold", color: "rgba(13,44,90,1)", textAlign: "center", width: "12%" }}>
                            <Box className="d-flex align-items-center justify-content-center">
                              Plan Name
                              <SortIcon sx={{ ml: 0.5, fontSize: "14px", color: "rgba(13,44,90,0.7)" }} />
                            </Box>
                          </TableCell>
                          <TableCell sx={{ fontWeight: "bold", color: "rgba(13,44,90,1)", textAlign: "center", width: "10%" }}>
                            <Box className="d-flex align-items-center justify-content-center">
                              Duration
                              <SortIcon sx={{ ml: 0.5, fontSize: "14px", color: "rgba(13,44,90,0.7)" }} />
                            </Box>
                          </TableCell>
                          <TableCell sx={{ fontWeight: "bold", color: "rgba(13,44,90,1)", textAlign: "center", width: "9%" }}>
                            <Box className="d-flex align-items-center justify-content-center">
                              KM Limit
                              <SortIcon sx={{ ml: 0.5, fontSize: "14px", color: "rgba(13,44,90,0.7)" }} />
                            </Box>
                          </TableCell>
                          <TableCell sx={{ fontWeight: "bold", color: "rgba(13,44,90,1)", textAlign: "center", width: "10%" }}>
                            <Box className="d-flex align-items-center justify-content-center">
                              Price
                              <SortIcon sx={{ ml: 0.5, fontSize: "14px", color: "rgba(13,44,90,0.7)" }} />
                            </Box>
                          </TableCell>
                          <TableCell sx={{ fontWeight: "bold", color: "rgba(13,44,90,1)", textAlign: "center", width: "12%" }}>
                            <Box className="d-flex align-items-center justify-content-center">
                              Processing Fees
                              <SortIcon sx={{ ml: 0.5, fontSize: "14px", color: "rgba(13,44,90,0.7)" }} />
                            </Box>
                          </TableCell>
                          <TableCell sx={{ fontWeight: "bold", color: "rgba(13,44,90,1)", textAlign: "center", width: "13%" }}>
                            <Box className="d-flex align-items-center justify-content-center">
                              Refundable Amount
                              <SortIcon sx={{ ml: 0.5, fontSize: "14px", color: "rgba(13,44,90,0.7)" }} />
                            </Box>
                          </TableCell>
                          <TableCell sx={{ fontWeight: "bold", color: "rgba(13,44,90,1)", textAlign: "center", width: "12%" }}>
                            <Box className="d-flex align-items-center justify-content-center">
                              Template
                              <SortIcon sx={{ ml: 0.5, fontSize: "14px", color: "rgba(13,44,90,0.7)" }} />
                            </Box>
                          </TableCell>
                          <TableCell sx={{ fontWeight: "bold", color: "rgba(13,44,90,1)", width: "10%", textAlign: "center" }}>
                            <Box className="d-flex align-items-center justify-content-center">
                              Status
                              <SortIcon sx={{ ml: 0.5, fontSize: "14px", color: "rgba(13,44,90,0.7)" }} />
                            </Box>
                          </TableCell>
                          <TableCell sx={{ fontWeight: "bold", color: "rgba(13,44,90,1)", width: "8%", textAlign: "center" }}>
                            <Box className="d-flex align-items-center justify-content-center">
                              Popular
                              <SortIcon sx={{ ml: 0.5, fontSize: "14px", color: "rgba(13,44,90,0.7)" }} />
                            </Box>
                          </TableCell>
                          <TableCell
                            sx={{
                              fontWeight: "bold",
                              color: "rgba(13,44,90,1)",
                              textAlign: "center",
                              width: "12%",
                              minWidth: "150px"
                            }}
                          >
                            Action
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody sx={{
                        "& .MuiTableCell-root": {
                          padding: "8px 12px",
                          borderBottom: "1px solid rgba(0,0,0,0.08)",
                          fontSize: "13px",
                          verticalAlign: "middle",
                          textAlign: "center"
                        },
                        "& .MuiTableRow-root:hover": {
                          backgroundColor: "rgba(13,44,90,0.02)"
                        }
                      }}>
                        <PlanCreateForm
                          newPlan={newPlan}
                          planStatusOptions={planStatusOptions}
                          templates={templates}
                          editingPlanId={editingPlanId}
                          loading={loading}
                          onNewPlanChange={handleNewPlanChange}
                          onAddPlan={handleAddPlan}
                          section={vehiclemodelid}
                        />
                      </TableBody>
                    </Table>
                  </TableContainer>

                  <Box 
                    className="d-flex justify-content-end mt-4"
                    sx={{
                      padding: "16px 0",
                      borderTop: "2px solid rgba(13,44,90,0.1)",
                      backgroundColor: "rgba(248, 249, 250, 0.3)"
                    }}
                  >
                    <Button
                      variant="contained"
                      startIcon={<AddIcon />}
                      onClick={() => handleAddPlan(vehiclemodelid)}
                      disabled={editingPlanId !== null || loading}
                      sx={{
                        backgroundColor: "rgba(13,44,90,1)",
                        minWidth: "120px",
                        height: "40px",
                        borderRadius: "8px",
                        fontSize: "14px",
                        fontWeight: 600,
                        textTransform: "none",
                        boxShadow: "0 2px 4px rgba(13,44,90,0.3)",
                        "&:hover": { 
                          backgroundColor: "rgba(13,44,90,0.9)",
                          boxShadow: "0 4px 8px rgba(13,44,90,0.4)"
                        },
                        "&:disabled": { 
                          backgroundColor: "rgba(13,44,90,0.5)",
                          boxShadow: "none"
                        },
                      }}
                    >
                      Add Plan
                    </Button>
                  </Box>
                </Box>
              )}
            </Box>
          </Paper>
        );
      })}


      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={cancelDelete}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            Are you sure you want to delete this plan? This action cannot be
            undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={cancelDelete}
            disabled={deletingPlanId !== null}
            sx={{ color: "rgba(13, 44, 90, 1)" }}
          >
            Cancel
          </Button>
          <Button
            onClick={confirmDeletePlan}
            disabled={deletingPlanId !== null}
            variant="contained"
            sx={{
              backgroundColor: "rgba(197, 0, 0, 1)",
              "&:hover": { backgroundColor: "rgba(197, 0, 0, 0.8)" },
            }}
          >
            {deletingPlanId !== null ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              "Yes, Delete"
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default EditPlanPage;
